import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { getNonRegistrantRegistrantAccountsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type { NonRegistrantRegistrantAccountListItemSchema } from "@/hera-api/client/types.gen";
import {
  buildRegistrantSelectOptions,
  isPrimaryRegistrantAccount,
} from "@/lib/registrant-display";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

const NO_REGISTRANT_VALUE = "__no_registrant__";

export interface RegistrantEmailAssignmentProps {
  registrantAccountId: string | null;
  onRegistrantAccountIdChange: (id: string | null) => void;
  email: string;
  onEmailChange: (email: string) => void;
  enabled?: boolean;
  onBlockedChange?: (blocked: boolean) => void;
}

export function registrantEmailBlocksSubmit(
  email: string,
  accounts: NonRegistrantRegistrantAccountListItemSchema[] | undefined,
): boolean {
  const trimmed = email.trim();
  if (!trimmed) return false;
  if (!trimmed.includes("@")) return true;
  const match = (accounts ?? []).find(
    (account) => account.email.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return match != null && !isPrimaryRegistrantAccount(match);
}

export function RegistrantEmailAssignment({
  registrantAccountId,
  onRegistrantAccountIdChange,
  email,
  onEmailChange,
  enabled = true,
  onBlockedChange,
}: RegistrantEmailAssignmentProps) {
  const accountsQuery = useQuery({
    ...getNonRegistrantRegistrantAccountsOptions({
      query: { include_deleted: false },
    }),
    enabled,
  });
  const accounts = accountsQuery.data?.registrant_accounts;
  const registrantOptions = useMemo(
    () => [
      { value: NO_REGISTRANT_VALUE, label: "No registrant" },
      ...buildRegistrantSelectOptions(
        (accounts ?? []).filter(isPrimaryRegistrantAccount),
      ),
    ],
    [accounts],
  );
  const trimmed = email.trim();
  const match = (accounts ?? []).find(
    (account) => account.email.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  const blocked = registrantEmailBlocksSubmit(email, accounts);
  useEffect(() => {
    onBlockedChange?.(blocked);
  }, [blocked, onBlockedChange]);

  const emailMessage = !trimmed
    ? null
    : !trimmed.includes("@")
      ? "Enter a valid email address."
      : match && !isPrimaryRegistrantAccount(match)
        ? "This email belongs to a secondary user. Use the primary registrant account email."
        : match
          ? `This email matches ${match.email}. That account will be used.`
          : "This email is not in the system. A registrant account will be created for it.";

  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        <Label>Linked registrant</Label>
        <Select
          options={registrantOptions}
          value={[registrantAccountId ?? NO_REGISTRANT_VALUE]}
          onValueChange={(value) => {
            const id = value[0];
            onRegistrantAccountIdChange(
              id && id !== NO_REGISTRANT_VALUE ? id : null,
            );
            if (id && id !== NO_REGISTRANT_VALUE) onEmailChange("");
          }}
          multiple={false}
          placeholder="No registrant"
          searchPlaceholder="Search by email, company, or name…"
          emptyMessage="No registrant found."
          disabled={trimmed.length > 0}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="registrant-email-assignment">Or assign by email</Label>
        <Input
          id="registrant-email-assignment"
          type="email"
          value={email}
          disabled={registrantAccountId != null}
          placeholder="name@example.com"
          onChange={(event) => {
            onEmailChange(event.target.value);
            if (event.target.value.trim()) onRegistrantAccountIdChange(null);
          }}
        />
        {emailMessage ? (
          <p
            className={
              match && !isPrimaryRegistrantAccount(match)
                ? "text-sm text-destructive"
                : "text-sm text-muted-foreground"
            }
          >
            {emailMessage}
          </p>
        ) : null}
      </div>
    </div>
  );
}
